import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { PageEmpty } from 'components/PageEmpty';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { filterArrayEmpty, safeAreaBottomIsRequired } from 'lib/riskbase';
import { theme } from 'lib/theme';
import { orderBy } from 'lodash';
import React from 'react';
import { View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  doAseSetupSignatoriesOrder,
  doAseSetupSignatoryDelete,
} from 'store/actions';
import { selectRemoteAssessmentSignatories } from 'store/remote/selectors';

export const SignatoriesSetupModal: React.FC<
  ModalScreenProps<ModalScreens.AseSetupSignatories>
> = () => {
  const dispatch = useDispatch();
  const { bottom } = useSafeAreaInsets();

  const signatories = orderBy(
    Object.values(useSelector(selectRemoteAssessmentSignatories) || {}),
    'ordering',
  );

  const handleClose = () => {
    dispatch(NAVIGATE_MODAL_BACK());
  };

  const handleOpenSignatory = (signatory: (typeof signatories)[number]) => {
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.AseSetupSignatory,
        params: {
          signatory,
        },
      }),
    );
  };

  const handleCreateSignatory = () => {
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.AseSetupSignatory,
        params: {
          signatory: null,
        },
      }),
    );
  };

  const handleReorderSignatories = () => {
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.REORDER,
        params: {
          title: 'Reorder Signatories',
          options: signatories.map((signatory) => ({
            value: signatory.id,
            name: signatory.name,
          })),
          action: doAseSetupSignatoriesOrder({ order: [] }),
        },
        size: 'small',
      }),
    );
  };

  const handleDeleteSignatory = (signatory: (typeof signatories)[number]) =>
    dispatch(
      doAseSetupSignatoryDelete({ id: signatory.id, name: signatory.name }),
    );

  useEsc(handleClose);

  return (
    <>
      <PageScrollView
        header={
          <NavigationHeader
            heading="Signatories"
            onPressSaveButton={handleClose}
            saveButtonText="Done"
          />
        }
      >
        {signatories.length ? (
          <Card>
            {signatories.map((signatory) => {
              const value = [
                signatory.requireOnSubmit ? 'Required to Submit' : null,
                signatory.allowUnavailable ? 'Allow "Not Available"' : null,
              ].filter(filterArrayEmpty);
              return (
                <CardRow
                  key={signatory.id}
                  label={signatory.name}
                  label2={signatory.declaration}
                  label2NumberOfLines={2}
                  labelAlignTop
                  value={value.length ? value.join(', ') : ''}
                  onPress={() => handleOpenSignatory(signatory)}
                  showMenuOnContext
                  menuOptions={[
                    {
                      text: 'Open Signatory',
                      onPress: () => handleOpenSignatory(signatory),
                    },
                    '-',
                    {
                      text: 'Delete Signatory',
                      onPress: () => handleDeleteSignatory(signatory),
                      destructive: true,
                      icon: ICONS.TrashCanLight,
                    },
                  ]}
                />
              );
            })}
          </Card>
        ) : (
          <PageEmpty
            icon={ICONS.SignatureLight}
            text="No Signatories"
            style={{ flex: 1 }}
          />
        )}
      </PageScrollView>
      <View
        style={{
          paddingTop: theme.paddingHorizontal,
          paddingBottom:
            theme.paddingHorizontal +
            (safeAreaBottomIsRequired(true) ? bottom : 0),
          borderTopWidth: 1,
          borderTopColor: '#dddddd',
        }}
      >
        <Card margin={false} contentHorizontal>
          <CardRow
            label="Add"
            icon={ICONS.PlusLight}
            labelColor={theme.colors.blue}
            iconColor={theme.colors.blue}
            onPress={handleCreateSignatory}
            style={{ flex: 1 }}
            compact
          />
          {signatories.length > 1 && (
            <CardRow
              label="Reorder"
              icon={ICONS.GripLinesLight}
              labelColor={theme.colors.blue}
              iconColor={theme.colors.blue}
              onPress={handleReorderSignatories}
              style={{ flex: 1 }}
              compact
            />
          )}
        </Card>
      </View>
    </>
  );
};
